import { useEffect, useState } from 'react';
import {
  Badge, Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { fetchPurchaseDetail, sendPurchaseEmail } from '../api/purchasing';
import type { PurchaseDetail, PurchaseDetailLine, User } from '../api/types';
import { t } from '../lib/i18n';
import { payablesPerms } from '../lib/payablesPerms';
import { canEmailPO } from '../lib/roles';
import { usePagination } from '../lib/usePagination';
import { buildTableCsvBlob, buildTablePdfBlob } from '../lib/exportTable';
import type { ExportColumn } from '../lib/exportTable';
import ErrorRetry from './ErrorRetry';
import ExportButtons from './ExportButtons';
import IconButton from './IconButton';
import Modal from './Modal';
import { BackIcon } from './icons';
import PayableUploadModal from './PayableUploadModal';
import Pagination from './Pagination';
import SendPurchaseEmailModal from './SendPurchaseEmailModal';
import TableSkeleton from './TableSkeleton';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const fmtQty = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

// Shared CSV/PDF column spec for the lines table — CSV keeps raw numbers,
// the PDF gets the on-screen money formatting (mirrors Sales.tsx's
// SALES_EXPORT). Also feeds the "Enviar por correo" attachments (JCD-23) so
// the emailed files are byte-identical to what the download buttons produce.
const LINE_COLUMNS: ExportColumn<PurchaseDetailLine>[] = [
  { label: 'Línea', value: (r) => r.line },
  { label: 'Parte', value: (r) => r.part },
  { label: 'Descripción', value: (r) => r.pdesc },
  { label: 'Cant. ord', value: (r) => r.qtyord, numeric: true },
  { label: 'Cant. rec', value: (r) => r.qtyrec, numeric: true },
  { label: 'Costo unit.', value: (r) => r.pcost_mxn, numeric: true, pdf: (r) => fmtMxn.format(r.pcost_mxn) },
  { label: 'Subtotal', value: (r) => r.ext_mxn, numeric: true, pdf: (r) => fmtMxn.format(r.ext_mxn) },
];

function statusBadge(s: PurchaseDetail['header']['status']) {
  const color = s === 'closed' ? 'emerald' : s === 'partial' ? 'amber' : 'gray';
  const label = t.purchasing.purchases.status[s];
  return <Badge color={color}>{label}</Badge>;
}

interface Props {
  sid: number;
  onum: number;
  onClose: () => void;
  /** Supplied by DrilldownHost when this modal sits on top of another (JCD-59). */
  onBack?: () => void;
}

export default function PurchaseDetailModal({ sid, onum, onClose, onBack }: Props) {
  const [data, setData] = useState<PurchaseDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Payables entry point: uploading a supplier invoice from the PO it belongs
  // to is the natural moment, and it pre-fills the proveedor + onum context.
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  useEffect(() => { currentUser().then(setUser).catch(() => setUser(null)); }, []);
  const apPerms = payablesPerms(user);
  const canEmail = !!user && canEmailPO(user);

  const load = () => {
    setErr(null);
    setData(null);
    fetchPurchaseDetail(sid, onum)
      .then(setData)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [sid, onum]);

  // Pagination only affects what's rendered; exports below always use the
  // full data.lines set, matching usePagination's "CSV/PDF exports keep the
  // full set" contract.
  const pg = usePagination(data?.lines);

  const exportMeta = data ? [`OC #${onum} · Tienda ${sid}`, `${data.header.sup_id} · ${data.header.sup_name}`] : undefined;

  async function handleSendEmail(to: string, includePdf: boolean) {
    if (!data) return;
    const csv = buildTableCsvBlob(LINE_COLUMNS, data.lines);
    const pdf = includePdf
      ? buildTablePdfBlob({
          title: t.purchasing.purchases.detail.title,
          meta: exportMeta,
          printedBy: user?.username ?? '',
          filename: `orden_compra_${sid}_${onum}`,
          columns: LINE_COLUMNS,
          rows: data.lines,
        })
      : undefined;
    await sendPurchaseEmail(sid, onum, { to, csv, pdf });
  }

  return (
    <Modal
      onClose={onClose}
      ariaLabel={t.purchasing.purchases.detail.title}
      role="dialog"
      panelClassName="max-w-4xl p-5 space-y-4"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-2">
          {onBack && (
            <IconButton onClick={onBack} label={t.drilldown.back} size="sm">
              <BackIcon />
            </IconButton>
          )}
          <div>
            <Title>{t.purchasing.purchases.detail.title}</Title>
            <Text className="text-tremor-content text-sm">
              OC #{onum} · Tienda {sid}
            </Text>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButtons
            title={t.purchasing.purchases.detail.title}
            filename={`orden_compra_${sid}_${onum}`}
            meta={exportMeta}
            printedBy={user?.username ?? ''}
            columns={LINE_COLUMNS}
            rows={data?.lines}
          />
          {canEmail && (
            <Button
              size="xs"
              variant="secondary"
              disabled={!data || data.lines.length === 0}
              onClick={() => setEmailing(true)}
            >
              {t.purchasing.purchases.email.button}
            </Button>
          )}
          <Button variant="light" onClick={onClose}>{t.common.close}</Button>
        </div>
      </div>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && !data && <TableSkeleton rows={6} cols={5} />}

      {data && (
        <>
          <Card>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Info label={t.purchasing.purchases.cols.date}  value={data.header.cdate} />
              <Info label={t.purchasing.purchases.cols.sup}   value={`${data.header.sup_id} · ${data.header.sup_name}`} />
              <Info label={t.purchasing.purchases.cols.cost}  value={fmtMxn.format(data.header.totord_mxn)} />
              <Info label={t.purchasing.purchases.cols.status} value={statusBadge(data.header.status)} />
              <Info label={t.purchasing.purchases.cols.qtyOrd} value={fmtQty.format(data.header.totunit)} />
              <Info label={t.purchasing.purchases.cols.qtyRcv} value={fmtQty.format(data.header.rec_units)} />
              <Info label="Recibido (MXN)" value={fmtMxn.format(data.header.rec_ord_mxn)} />
              <Info label="Posted" value={data.header.posted ?? '—'} />
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <Text className="px-4 pt-3 font-semibold">{t.purchasing.purchases.detail.lines}</Text>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Línea</TableHeaderCell>
                  <TableHeaderCell>Parte</TableHeaderCell>
                  <TableHeaderCell>Descripción</TableHeaderCell>
                  <TableHeaderCell className="text-right">Cant. ord</TableHeaderCell>
                  <TableHeaderCell className="text-right">Cant. rec</TableHeaderCell>
                  <TableHeaderCell className="text-right">Costo unit.</TableHeaderCell>
                  <TableHeaderCell className="text-right">Subtotal</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pg.pageRows.map((ln, i) => (
                  <TableRow key={`${ln.line}-${ln.part}-${i}`}>
                    <TableCell>{ln.line}</TableCell>
                    <TableCell className="font-medium">{ln.part}</TableCell>
                    <TableCell>{ln.pdesc}</TableCell>
                    <TableCell className="text-right">{fmtQty.format(ln.qtyord)}</TableCell>
                    <TableCell className="text-right">{fmtQty.format(ln.qtyrec)}</TableCell>
                    <TableCell className="text-right">{fmtMxn.format(ln.pcost_mxn)}</TableCell>
                    <TableCell className="text-right">{fmtMxn.format(ln.ext_mxn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination {...pg} className="px-4" />
          </Card>

          {data.receipts.length > 0 && (
            <Card className="p-0 overflow-hidden">
              <Text className="px-4 pt-3 font-semibold">{t.purchasing.purchases.detail.receipts}</Text>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>#</TableHeaderCell>
                    <TableHeaderCell>Fecha</TableHeaderCell>
                    <TableHeaderCell>ID</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.receipts.map((r) => (
                    <TableRow key={r.receive_no}>
                      <TableCell>{r.receive_no}</TableCell>
                      <TableCell>{r.xfr_stk ?? '—'}</TableCell>
                      <TableCell>{r.id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}

      {apPerms.canCapture ? (
        <Button className="mt-3" onClick={() => setUploading(true)}>
          {t.payables.upload}
        </Button>
      ) : null}

      {uploading ? (
        <PayableUploadModal
          sid={sid}
          onum={onum}
          onClose={() => setUploading(false)}
          onDone={() => setUploading(false)}
        />
      ) : null}

      {emailing && data ? (
        <SendPurchaseEmailModal
          supId={data.header.sup_id}
          onSend={handleSendEmail}
          onClose={() => setEmailing(false)}
        />
      ) : null}
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong font-semibold mt-0.5">{value}</div>
    </div>
  );
}
